var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
function isPresetMuteConfig(cfg) {
    return typeof cfg !== 'number' && 'mutePreset' in cfg;
}
export var ModelNumber;
(function (ModelNumber) {
    ModelNumber["ANIUSB"] = "-N1ipeuSwhU2SKi70XS6";
    ModelNumber["IntelliMixRoom"] = "-N1ipq31i0gIcXoe1UhO";
    ModelNumber["MXA910"] = "-N1ipjlaoycSCLRfpnnQ";
    ModelNumber["P300"] = "-N1ipa38Yz2C6UoldSeR";
})(ModelNumber || (ModelNumber = {}));
const channelConfigurations = {
    [ModelNumber.ANIUSB]: [
        { name: 'Dante Input', count: 4, type: 'danteInput' },
        { name: 'Analog Input', type: 'analogInput' },
        { name: 'USB Input', type: 'usbInput' },
        { name: 'Dante Output', count: 2, type: 'danteOutput' },
        { name: 'Analog Output', type: 'analogInput' },
        { name: 'USB Output', type: 'usbOutput' },
    ],
    [ModelNumber.IntelliMixRoom]: [
        { name: 'Mic Input', count: 16, type: 'danteMicInput' },
        { name: 'Dante Input', count: 8, type: 'danteInput' },
        { name: 'Virtual Audio Input', type: 'virtualInput' },
        { name: 'PC Input', type: 'usbInput' },
        { name: 'Dante Output', count: 8, type: 'danteOutput' },
        { name: 'Virtual Audio Output', type: 'virtualOutput' },
        { name: 'PC Output', type: 'usbOutput' },
        { name: 'Automix Output', type: 'automixerOutput' },
    ],
    [ModelNumber.MXA910]: [
        { name: 'Dante Output', count: 8, type: 'danteOutput' },
        { name: 'Automix Output', type: 'automixerOutput' },
        { name: 'AEC Reference', type: 'aecReference' },
    ],
    [ModelNumber.P300]: [
        { name: 'Mic Input', count: 8, type: 'danteMicInput' },
        { name: 'Dante Input', count: 2, type: 'danteInput' },
        { name: 'Analog Input', count: 2, type: 'analogInput' },
        { name: 'USB Input', type: 'usbInput' },
        { name: 'Mobile Input', type: 'mobileInput' },
        { name: 'Dante Output', count: 2, type: 'danteOutput' },
        { name: 'Analog Output', count: 2, type: 'analogInput' },
        { name: 'USB Output', type: 'usbOutput' },
        { name: 'Mobile Output', type: 'mobileOutput' },
        { name: 'Mix Out', type: 'automixerOutput' },
        { name: 'AEC Reference', type: 'aecReference' },
        { name: 'Dante Output', count: 6, type: 'danteOutput', startNumbering: 3 },
    ],
};
const suggestedInputTypes = [
    'analogInput',
    'danteInput',
    'danteMicInput',
    'danteOutput',
    'mobileInput',
    'usbInput',
    'virtualInput',
];
const suggestedMicsTypes = [
    'analogInput',
    'automixerOutput',
    'danteInput',
    'danteMicInput',
    'danteOutput',
    'mobileInput',
    'usbInput',
    'usbOutput',
    'virtualInput',
];
const suggestedOutputTypes = [
    'analogInput',
    'analogOutput',
    'automixerOutput',
    'danteInput',
    'danteMicInput',
    'danteOutput',
    'mobileInput',
    'mobileOutput',
    'usbInput',
    'usbOutput',
    'virtualInput',
    'virtualOutput',
];
const suggestedTypes = {
    mics: suggestedMicsTypes,
    micsMuteStatus: suggestedMicsTypes,
    amplifier: suggestedOutputTypes,
    assistiveListeningMute: suggestedOutputTypes,
    assistiveListeningMuteStatus: suggestedOutputTypes,
    assistiveListeningVolume: suggestedOutputTypes,
    audioInputMute: suggestedInputTypes,
    audioInputMuteStatus: suggestedInputTypes,
    audioInputVolume: suggestedInputTypes,
    overflowRoomMute: suggestedOutputTypes,
    overflowRoomMuteStatus: suggestedOutputTypes,
    overflowRoomVolume: suggestedOutputTypes,
    soundReinforcementMute: suggestedOutputTypes,
    soundReinforcementMuteStatus: suggestedOutputTypes,
    soundReinforcementVolume: suggestedOutputTypes,
    telephony: [],
    receiveGain: [],
    preset: [],
};
export class P300 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = {};
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '< GET DEVICE_ID >',
                succeed: /^< REP DEVICE_ID {.+}/,
            },
        ];
        this.mutePresets = {};
        this.unmutePresets = {};
        this.presets = {};
        this.timeouts = new Timeouts();
        this.flashTimeout = this.timeouts.create();
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.getChannelNames(tags);
                yield this.getPresetNames(tags);
                return this.makeDetectedResults();
            }),
            asyncHandlers: [
                {
                    regExp: /^< REP (\d+) CHAN_NAME {(.+)}/,
                    callback: (match) => {
                        const key = match[1];
                        const channel = this.channels[key];
                        if (channel === undefined) {
                            this.log.error(`unexpected channel: ${key}`);
                            return;
                        }
                        channel.name = match[2].trim();
                        this.state.detected.update(this.makeDetectedResults());
                    },
                },
                {
                    regExp: /^< REP PRESET(\d+) {(.+)}/,
                    callback: (match) => {
                        const key = match[1].padStart(2, '0');
                        const preset = this.presets[key];
                        if (preset === undefined) {
                            this.log.error(`unexpected preset: ${key}`);
                            return;
                        }
                        preset.name = match[2].trim();
                        this.state.detected.update(this.makeDetectedResults());
                    },
                },
            ],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`< GET FW_VER >`, /^< REP FW_VER {(.+)}/, tags);
                return match[1].trim();
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const param = channel.type === 'automixerOutput' ? 'AUTOMXR_MUTE' : 'AUDIO_MUTE';
                const match = yield this.socket.send(`< GET ${key} ${param} >`, new RegExp(`^< REP ${key} ${param} (ON|OFF)`), tags);
                return match[1] === 'ON';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.mutePresets[key]) {
                    const presetKey = value ? this.mutePresets[key] : this.unmutePresets[key];
                    // When running a preset, the P300 responds first with the values that changed,
                    // then with the "REP PRESET" response. We await for the "REP PRESET" response and
                    // expect that the async handler will update the mute state by the time it
                    // resolves. We then return the current state since it should have been updated by
                    // the async handler.
                    yield this.socket.send(`< SET PRESET ${presetKey} >`, new RegExp(`^< REP PRESET ${presetKey}`), tags);
                    return this.state.mutes.values[key];
                }
                const channel = this.channels[key];
                const param = channel.type === 'automixerOutput' ? 'AUTOMXR_MUTE' : 'AUDIO_MUTE';
                const match = yield this.socket.send(`< SET ${key} ${param} ${value ? 'ON' : 'OFF'} >`, new RegExp(`^< REP ${key} ${param} (ON|OFF)`), tags);
                return match[1] === 'ON';
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    regExp: /^< REP (\d+) (AUTOMXR_MUTE|AUDIO_MUTE) (ON|OFF)/,
                    callback: (match) => {
                        const key = match[1];
                        const channel = this.channels[key];
                        if (channel === undefined) {
                            this.log.error(`no channel for key: ${key}`);
                            return;
                        }
                        this.state.mutes.update(key, match[3] === 'ON');
                    },
                },
            ],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(`< GET ${key} AUDIO_GAIN_HI_RES >`, new RegExp(`^< REP ${key} AUDIO_GAIN_HI_RES (\\d+)`), tags);
                return parseInt(match[1], 10);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`< SET ${key} AUDIO_GAIN_HI_RES ${value} >`, new RegExp(`^< REP ${key} AUDIO_GAIN_HI_RES (\\d+)`), tags);
                return parseInt(match[1], 10);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 1220, min: 980, round: 1 };
                return { max: 1220, min: 720, round: 1 }; // volume
            }),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    regExp: /^< REP (\d+) AUDIO_GAIN_HI_RES (\d+)/,
                    callback: (match) => {
                        const key = match[1];
                        const channel = this.channels[key];
                        if (channel === undefined) {
                            this.log.error(`no channel for key: ${key}`);
                            return;
                        }
                        this.state.volumes.update(key, parseInt(match[2], 10));
                    },
                },
            ],
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const tags = ['get.deviceId', 'poll'];
                    yield this.socket.send(`< GET DEVICE_ID >`, /^< REP DEVICE_ID/, tags);
                }),
            },
        ];
        for (let i = 0; i < 10; i++) {
            const key = (i + 1).toString().padStart(2, '0');
            this.presets[key] = {
                channelNumber: i + 1,
                name: '',
            };
        }
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll([this.flashTimeout]);
            this.mutePresets = {};
            this.unmutePresets = {};
            if (this.modelNumber === undefined || this.modelNumber !== config.modelNumber) {
                this.modelNumber = (_a = config.modelNumber) !== null && _a !== void 0 ? _a : ModelNumber.P300;
                this.channels = {};
                let channelNumber = 1;
                let channelConfiguration = channelConfigurations[this.modelNumber];
                if (channelConfiguration === undefined) {
                    this.log.error(`no channel configuration for model number "${this.modelNumber}"`);
                    channelConfiguration = channelConfigurations[ModelNumber.P300];
                }
                channelConfiguration.forEach(chan => {
                    var _a, _b;
                    const numChannels = (_a = chan.count) !== null && _a !== void 0 ? _a : 1;
                    for (let n = 0; n < numChannels; n++) {
                        const key = channelNumber.toString().padStart(2, '0');
                        this.channels[key] = {
                            channelNumber,
                            // IntelliMixRoom systems and automixerOutput/aecReference channels don't
                            // support querying the channel names.
                            isNameInitialized: chan.type === 'automixerOutput' ||
                                chan.type === 'aecReference' ||
                                this.modelNumber === ModelNumber.IntelliMixRoom,
                            name: numChannels === 1
                                ? chan.name
                                : `${chan.name} ${n + 1 + ((_b = chan.startNumbering) !== null && _b !== void 0 ? _b : 0)}`,
                            type: chan.type,
                        };
                        channelNumber++;
                    }
                });
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            if (result === 'connected') {
                // The Intellimix Room doesn't support the flash command and responds with an error.
                const match = yield this.socket.send(`< SET FLASH ON >`, /^< REP (FLASH ON)|^< REP ERR/);
                if (match[1]) {
                    this.flashTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                        // We need to catch transport errors here since the promise is running on a
                        // timeout which isn't handled by the driver framework.
                        try {
                            yield this.socket.send(`< SET FLASH OFF >`, /^< REP FLASH OFF/);
                        }
                        catch (error) {
                            if (!(error instanceof TransportError)) {
                                this.log.error(error);
                            }
                        }
                    }), 3000);
                }
            }
            this.channels = {};
            this.modelNumber = undefined;
            return result;
        });
    }
    // other class methods
    getChannelNames(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Object.values(this.channels).some(chan => !chan.isNameInitialized))
                return;
            let timeout;
            let id;
            const promise = new Promise((resolve, reject) => {
                const timeoutMs = 2000;
                const onComplete = () => {
                    if (timeout === null || timeout === void 0 ? void 0 : timeout.isRunning)
                        timeout === null || timeout === void 0 ? void 0 : timeout.clear();
                    if (id !== undefined)
                        this.socket.unregisterAsync(id);
                    resolve();
                };
                timeout = this.timeouts.set(onComplete, timeoutMs);
                id = this.socket.registerAsync({
                    regExp: /^< REP (\d+) CHAN_NAME {(.+)}/,
                    callback: (match) => {
                        const key = match[1];
                        const channel = this.channels[key];
                        if (channel === undefined) {
                            this.log.error(`unexpected channel ${key}`);
                            return;
                        }
                        channel.name = match[2].trim();
                        channel.isNameInitialized = true;
                        timeout === null || timeout === void 0 ? void 0 : timeout.clear();
                        timeout = this.timeouts.set(onComplete, timeoutMs);
                        if (Object.values(this.channels).every(channel => channel.isNameInitialized)) {
                            onComplete();
                        }
                    },
                });
            });
            try {
                yield this.socket.sendAsync('< GET 00 CHAN_NAME >', tags);
            }
            catch (error) {
                timeout === null || timeout === void 0 ? void 0 : timeout.clear();
                if (id !== undefined)
                    this.socket.unregisterAsync(id);
            }
            return promise;
        });
    }
    getKey(config) {
        if (isPresetMuteConfig(config)) {
            return this.getKey(config.muteStatus);
        }
        return config.toString().padStart(2, '0');
    }
    getPresetNames(tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            for (const preset of Object.values(this.presets)) {
                const match = yield this.socket.send(`< GET PRESET${preset.channelNumber} >`, new RegExp(`^< REP PRESET${preset.channelNumber} {(.+)}|^< REP ERR`), tags);
                preset.name = (_a = match[1]) !== null && _a !== void 0 ? _a : `Preset ${preset.channelNumber}`;
            }
        });
    }
    makeDetectedResults() {
        const detected = [];
        for (const channel of Object.values(this.channels)) {
            if (channel.isNameInitialized) {
                detected.push({
                    channelNumber: channel.channelNumber,
                    filters: this.makeFilters(channel.type),
                    name: channel.name,
                    type: channel.type,
                });
            }
        }
        for (const preset of Object.values(this.presets)) {
            detected.push({
                channelNumber: preset.channelNumber,
                filters: this.makeFilters('preset'),
                name: preset.name,
                type: 'preset',
            });
        }
        return detected;
    }
    makeFilters(type) {
        const filters = {};
        for (const [key, types] of Object.entries(suggestedTypes)) {
            if (type === 'preset') {
                filters[key] = key === 'preset';
            }
            else {
                filters[key] = types.includes(type);
            }
        }
        return filters;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.channels[key];
            if (isPresetMuteConfig(config)) {
                this.mutePresets[key] = config.mutePreset.toString().padStart(2, '0');
                this.unmutePresets[key] = config.unmutePreset.toString().padStart(2, '0');
            }
            if (channel === undefined)
                return 'missing';
            if (channel.type === 'aecReference')
                return 'incompatible';
            return 'ok';
        });
    }
}
// static properties
P300.channelsConfigName = 'p300ControlChannels';
P300.driverType = 'socketDriver';
P300.key = '-L0oCNdkw3P7BJg9JwVr';
P300.socketOptions = [
    {
        commandExtractor: {
            terminators: ['>'],
        },
        port: 2202,
        sendTerminator: '',
    },
];
